import {
  MerchantStoreName,
  MerchantStoreSiteId,
} from "../../features/api-interfaces";
import { getMessage } from "../../features/error/messages";
import {
  isNull,
  isLengthFixedValid,
  isLengthValid,
  isHalfNumberValid,
  isKatakanaValid,
} from "../../utils/inputValidation";
import { COMMON_LABELS, DivisionDict } from "../default-constants";
import {
  CONTRACT_COMPREHENSIVE,
  CONTRACT_DIRECTLY,
  CONTRACT_ROUTE_SELECT_ITEMS,
  LABELS,
  RECONCILABLE_PERIOD,
} from "./constants";

/**
 * 加盟店取得結果をプルダウン用のオブジェクトに変換する.
 * 加盟店の重複は削除する.
 * @param {MerchantStoreName[]} merchants
 * @returns {DivisionDict[]} 変換された加盟店取得結果
 */
export const getMerchantStoreNameItems = (
  merchants: MerchantStoreName[] | undefined
): DivisionDict[] => {
  if (merchants !== undefined) {
    return merchants
      .filter(
        (merchant, index, self) =>
          index === self.findIndex((s) => s.tradeName === merchant.tradeName)
      )
      .map((merchant) => ({
        label: merchant.tradeName,
        value: merchant.tradeName,
      }));
  }
  return [];
};

/**
 * サイトID取得結果をプルダウン用のオブジェクトに変換する.
 * サイトIDの重複は削除する.
 * @param {MerchantStoreSiteId[]} siteIds
 * @returns {DivisionDict[]} 変換されたサイトID取得結果
 */
export const getMerchantStoreSiteIdItems = (
  siteIds: MerchantStoreSiteId[] | undefined
): DivisionDict[] => {
  if (siteIds !== undefined) {
    return siteIds
      .filter(
        (siteId, index, self) =>
          index === self.findIndex((s) => s.cpid === siteId.cpid)
      )
      .map((siteId) => ({
        label: siteId.cpid,
        value: siteId.cpid,
      }));
  }
  return [];
};

/**
 * 引数の契約形態のリストを基に契約形態の画面表示用のラベルを返却する
 * @param {number[] | undefined} contractStyles 契約形態のリスト
 * @returns {string} 契約形態の画面表示用のラベル
 */
export const getContractStyleLabel = (contractStyles: number[] | undefined) => {
  const directlyLabel = CONTRACT_ROUTE_SELECT_ITEMS.find(
    (item) => item.value === CONTRACT_DIRECTLY
  )!.label;
  const comprehensiveLabel = CONTRACT_ROUTE_SELECT_ITEMS.find(
    (item) => item.value === CONTRACT_COMPREHENSIVE
  )!.label;

  return contractStyles
    ?.map((contractStyles) => {
      if (contractStyles === Number(CONTRACT_DIRECTLY)) {
        return directlyLabel + LABELS.CONTRACT;
      } else if (contractStyles === Number(CONTRACT_COMPREHENSIVE)) {
        return comprehensiveLabel + LABELS.CONTRACT;
      } else {
        return COMMON_LABELS.BLANK;
      }
    })
    .join(" ");
};

export const checkInput = (
  target: string,
  setErrorMsg: any,
  doCheckNull: boolean,
  doCheckFixedLength: boolean,
  doCheckLength: boolean,
  doCheckHalfNumber: boolean,
  doCheckKatakana: boolean,
  strFixedLength: number,
  strLength: number
) => {
  // 必須
  if (doCheckNull && isNull(target)) {
    setErrorMsg(getMessage("KGRERR055").label);
    return false;
  }
  // 固定文字長
  if (doCheckFixedLength && !isLengthFixedValid(target, strFixedLength)) {
    setErrorMsg(getMessage("KGRERR073", strFixedLength).label);
    return false;
  }
  // 文字長以内
  if (doCheckLength && !isLengthValid(target, strLength)) {
    setErrorMsg(getMessage("KGRERR058", strLength).label);
    return false;
  }
  // 半角数値
  if (doCheckHalfNumber && !isHalfNumberValid(target)) {
    setErrorMsg(getMessage("KGRERR006").label);
    return false;
  }
  // カタカナ
  if (doCheckKatakana && !isKatakanaValid(target)) {
    setErrorMsg(getMessage("KGRERR026").label);
    return false;
  }
  setErrorMsg("");
  return true;
};

/**
 * 画面表示用の売上高を返却する
 * @param {string | undefined} sales APIから受け取った売上高
 * @param {string | undefined} nowDate APIから受け取った売上高年度
 * @returns {string} 画面表示用の売上高
 */
export const getDisplaySales = (
  sales: string | undefined,
  nowDate: string | undefined
) => {
  let salesStr = "";
  if (sales !== undefined && sales !== null) {
    salesStr += sales + LABELS.BILLION_YEN;
  } else {
    salesStr += LABELS.BLANK_SPACE_ONE;
  }
  if (nowDate !== undefined && nowDate !== null) {
    salesStr +=
      LABELS.BLANK_SPACE_ONE +
      COMMON_LABELS.LEFT_PARENTHESIS +
      nowDate +
      LABELS.YEAR +
      COMMON_LABELS.RIGHT_PARENTHESIS;
  }

  return salesStr.replace(LABELS.BLANK_SPACE_ONE, "").length !== 0
    ? salesStr
    : LABELS.BLANK_SPACE_ONE;
};

/**
 * 突合可能期限ラベルを返却する.
 * 1: 翌々月末, 2: 90日後, 4: 367日
 * @param {string} reconcilablePeriodCode 突合可能期限コード
 * @returns {string} 突合可能期限ラベル
 */
export const getReconcilablePeriodLabel = (reconcilablePeriodCode: string) => {
  const { endOfNextNextMonth, ninetyDaysLater, oneYear } = RECONCILABLE_PERIOD;
  switch (reconcilablePeriodCode) {
    case endOfNextNextMonth.value:
      return endOfNextNextMonth.label;
    case ninetyDaysLater.value:
      return ninetyDaysLater.label;
    case oneYear.value:
      return oneYear.label;
    default:
      return "";
  }
};
